import { loadSpotifyIframeApi } from "./iframe-api.js";
import { buildSpotifyIframeSrc, parseSpotifySource } from "./source.js";
import { EMPTY_TEXT_TRACKS, EMPTY_TIME_RANGES, MediaError } from "@videojs/media";
import { MediaPlayedRangesMixin, createTimeRange } from "@videojs/media/dom";
import { createPublicPromise, tryCall } from "@videojs/utils/function";
import { deepEqual } from "@videojs/utils/object";
import { isNumber } from "@videojs/utils/predicate";
//#region src/adapter.ts
/**
* @fires sourcechange - Fired when `source` changes, either directly or by resolving a new `src`. Read `source` for the
*   new value.
*/
var SpotifyAdapter = class SpotifyAdapter extends MediaPlayedRangesMixin(EventTarget) {
	static {
		this.defaultProps = {
			src: "",
			autoplay: false,
			loop: false,
			controls: false,
			playsInline: true,
			preload: "metadata",
			poster: "",
			source: null
		};
	}
	#target = null;
	#controller = null;
	#controllerReady = false;
	#pendingLoad = false;
	#creatingController = false;
	#loadComplete = createPublicPromise();
	#attachId = 0;
	#src = SpotifyAdapter.defaultProps.src;
	#autoplay = SpotifyAdapter.defaultProps.autoplay;
	#loop = SpotifyAdapter.defaultProps.loop;
	#controls = SpotifyAdapter.defaultProps.controls;
	#playsInline = SpotifyAdapter.defaultProps.playsInline;
	#preload = SpotifyAdapter.defaultProps.preload;
	#poster = SpotifyAdapter.defaultProps.poster;
	#source = SpotifyAdapter.defaultProps.source;
	#paused = true;
	#ended = false;
	#seeking = false;
	#loaded = false;
	#currentTime = 0;
	#duration = NaN;
	#readyState = READY_STATE_HAVE_NOTHING;
	#error = null;
	#waiting = false;
	#pauseRequested = false;
	#closeToEnded = false;
	static {
		this.PLAYER_SOFTWARE_NAME = "spotify-audio";
	}
	/** Underlying Spotify iframe API controller (null until the API loads). */
	get engine() {
		return this.#controller;
	}
	/** The iframe holding the embed, which everything from the embed URL down is written to. */
	get target() {
		return this.#target;
	}
	/** Bind the iframe hosting the embed; the API and controller follow as soon as an embed URL resolves. */
	attach(target) {
		if (!target || this.#target === target) return;
		if (this.#target) this.detach();
		this.#target = target;
		this.#beginLoad();
		this.#createPlayer();
	}
	detach() {
		if (!this.#target) return;
		this.#attachId++;
		tryCall(() => {
			if (this.#controller) {
				this.#controller.iframeElement = createControllerPlaceholderFrame();
				this.#controller.destroy();
			}
		});
		this.#controller = null;
		this.#controllerReady = false;
		this.#pendingLoad = false;
		this.#creatingController = false;
		this.#target = null;
		this.#loadComplete.resolve();
		this.#resetState();
	}
	destroy() {
		this.detach();
		super.destroy();
	}
	get src() {
		return this.#src;
	}
	/** Spotify URL or URI. Setting it re-derives `source`, carrying its embed options over. */
	set src(value) {
		const { engine } = this.#source ?? {};
		const next = {
			...engine && { engine },
			...value && { src: value }
		};
		this.source = Object.keys(next).length > 0 ? next : null;
	}
	get currentSrc() {
		return this.#target?.getAttribute("src") ?? "";
	}
	get readyState() {
		return this.#readyState;
	}
	/** Reload the current source via the iframe API; deferred until the controller is ready. */
	async load() {
		if (!this.#controller || !this.#controllerReady) {
			this.#pendingLoad = !!this.#target;
			if (this.#target && !this.#controller && !this.#creatingController) {
				const load = this.#beginLoad();
				await Promise.resolve();
				if (load !== this.#loadComplete) return;
				this.#createPlayer();
			}
			return;
		}
		const load = this.#beginLoad();
		this.#resetState();
		this.dispatchEvent(new Event("emptied"));
		if (!this.#src) {
			load.resolve();
			tryCall(() => this.#controller?.pause());
			return;
		}
		this.dispatchEvent(new Event("loadstart"));
		const parsed = parseSpotifySource(this.#src);
		if (!parsed) {
			this.#error = new MediaError(`Unrecognized Spotify source: ${this.#src}`, MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED);
			this.dispatchEvent(new Event("error"));
			load.resolve();
			return;
		}
		const target = this.#target;
		const embedSrc = buildSpotifyIframeSrc(this.#src, this.#snapshotProps());
		const embeddedSrc = target?.getAttribute("src") ?? "";
		const rebuild = embedOptionsOf(embedSrc) !== embedOptionsOf(embeddedSrc) || isVideoEmbed(embedSrc) && embedPathOf(embedSrc) !== embedPathOf(embeddedSrc);
		if (target && embedSrc && rebuild) {
			target.src = embedSrc;
			return;
		}
		this.#controller.loadUri(`spotify:${parsed.type}:${parsed.id}`);
		const startTime = this.#source?.engine?.spotify?.t ?? parsed.startTime;
		if (isNumber(startTime)) this.currentTime = startTime;
	}
	#beginLoad() {
		this.#loadComplete.resolve();
		this.#loadComplete = createPublicPromise();
		return this.#loadComplete;
	}
	get paused() {
		return this.#paused;
	}
	get ended() {
		return this.#ended;
	}
	get seeking() {
		return this.#seeking;
	}
	async play() {
		await this.#loadComplete;
		if (!this.#src) return;
		this.#controller?.resume();
	}
	pause() {
		this.#pauseRequested = true;
		this.#controller?.pause();
	}
	get currentTime() {
		return this.#currentTime;
	}
	set currentTime(value) {
		if (this.#currentTime === value) return;
		this.#seeking = true;
		this.#closeToEnded = false;
		this.#ended = false;
		this.#currentTime = value;
		this.dispatchEvent(new Event("seeking"));
		this.dispatchEvent(new Event("timeupdate"));
		this.#afterLoad((controller) => controller.seek(value));
	}
	get duration() {
		return this.#duration;
	}
	get autoplay() {
		return this.#autoplay;
	}
	set autoplay(value) {
		this.#autoplay = value;
	}
	get loop() {
		return this.#loop;
	}
	set loop(value) {
		this.#loop = value;
	}
	get controls() {
		return this.#controls;
	}
	set controls(value) {
		this.#controls = value;
	}
	get playsInline() {
		return this.#playsInline;
	}
	set playsInline(value) {
		this.#playsInline = value;
	}
	get preload() {
		return this.#preload;
	}
	set preload(value) {
		this.#preload = value;
	}
	get poster() {
		return this.#poster;
	}
	set poster(value) {
		this.#poster = value;
	}
	/** Spotify URL or URI in `src`, plus embed options under `engine.spotify`. Replacing it re-derives `src`. */
	get source() {
		return this.#source;
	}
	set source(value) {
		const source = value ?? null;
		if (source === this.#source) return;
		const src = source?.src ?? "";
		const srcChanged = this.#src !== src;
		const engineChanged = !deepEqual(this.#source?.engine?.spotify ?? null, source?.engine?.spotify ?? null);
		this.#source = source;
		this.#src = src;
		if (srcChanged || engineChanged) this.load();
		this.dispatchEvent(new Event("sourcechange"));
	}
	get buffered() {
		return this.#currentTime > 0 ? createTimeRange(0, this.#currentTime) : EMPTY_TIME_RANGES;
	}
	get seekable() {
		return this.#duration > 0 && Number.isFinite(this.#duration) ? createTimeRange(0, this.#duration) : EMPTY_TIME_RANGES;
	}
	get error() {
		return this.#error;
	}
	/** Always empty: the embed exposes no captions or other text tracks. */
	get textTracks() {
		return EMPTY_TEXT_TRACKS;
	}
	#createPlayer() {
		const target = this.#target;
		if (!target || this.#controller || this.#creatingController) return false;
		if (!target.getAttribute("src")) {
			const initialSrc = buildSpotifyIframeSrc(this.#src, this.#snapshotProps());
			if (!initialSrc) {
				this.#loadComplete.resolve();
				return false;
			}
			target.src = initialSrc;
		}
		this.#creatingController = true;
		this.dispatchEvent(new Event("loadstart"));
		this.#createControllerApi(target);
		return true;
	}
	async #createControllerApi(target) {
		const attachId = this.#attachId;
		let api;
		try {
			api = await loadSpotifyIframeApi();
		} catch {
			if (this.#isStale(attachId)) return;
			this.#creatingController = false;
			this.#error = new MediaError("Failed to load the Spotify iframe API", MediaError.MEDIA_ERR_NETWORK);
			this.dispatchEvent(new Event("error"));
			this.#loadComplete.resolve();
			return;
		}
		if (this.#isStale(attachId) || this.#target !== target) return;
		const { referrerPolicy: _referrerPolicy, ...options } = this.#source?.engine?.spotify ?? {};
		const controller = await new Promise((resolve) => api.createController(createControllerPlaceholder(), options, resolve));
		if (this.#isStale(attachId) || this.#target !== target) {
			tryCall(() => controller.destroy());
			return;
		}
		controller.iframeElement = target;
		this.#controller = controller;
		this.#creatingController = false;
		this.#bindControllerEvents(controller, attachId);
	}
	#isStale(attachId) {
		return attachId !== this.#attachId;
	}
	#afterLoad(fn) {
		this.#loadComplete.then(() => {
			const controller = this.#controller;
			if (controller) tryCall(() => fn(controller));
		}, () => {});
	}
	#snapshotProps() {
		return {
			autoplay: this.#autoplay,
			loop: this.#loop,
			controls: this.#controls,
			playsInline: this.#playsInline,
			preload: this.#preload || SpotifyAdapter.defaultProps.preload,
			source: this.#source
		};
	}
	#resetState() {
		this.#currentTime = 0;
		this.#duration = NaN;
		this.#paused = !this.#autoplay;
		this.#ended = false;
		this.#readyState = READY_STATE_HAVE_NOTHING;
		this.#seeking = false;
		this.#loaded = false;
		this.#waiting = false;
		this.#pauseRequested = false;
		this.#closeToEnded = false;
		this.#error = null;
	}
	#onControllerReady() {
		this.#controllerReady = true;
		if (this.#pendingLoad) {
			this.#pendingLoad = false;
			this.load();
			return;
		}
		this.#onLoaded();
	}
	#onLoaded() {
		if (this.#loaded) return;
		this.#loaded = true;
		this.#readyState = READY_STATE_HAVE_METADATA;
		for (const type of [
			"loadedmetadata",
			"durationchange",
			"loadcomplete"
		]) this.dispatchEvent(new Event(type));
		this.#loadComplete.resolve();
		if (this.#autoplay) this.play();
	}
	#bindControllerEvents(controller, attachId) {
		controller.addListener("ready", () => {
			if (this.#isStale(attachId)) return;
			this.#onControllerReady();
		});
		controller.addListener("playback_update", (event) => {
			if (this.#isStale(attachId)) return;
			this.#onPlaybackUpdate(event.data);
		});
	}
	#onPlaybackUpdate(data) {
		if (this.#src && !this.#loaded) this.#onLoaded();
		if (this.#restartFromEnd(data)) return;
		this.#syncDuration(data.duration);
		this.#syncPosition(data.position);
		if (this.#syncPlayState(data)) return;
		this.#checkEnded();
	}
	#isStarting(data) {
		return data.isBuffering || !data.isPaused;
	}
	#restartFromEnd(data) {
		if (!this.#closeToEnded || !this.#paused || !this.#isStarting(data)) return false;
		this.#closeToEnded = false;
		this.currentTime = REPLAY_POSITION;
		return true;
	}
	#syncDuration(duration) {
		const seconds = duration / 1e3;
		if (seconds === this.#duration) return;
		this.#closeToEnded = false;
		this.#duration = seconds;
		this.dispatchEvent(new Event("durationchange"));
	}
	#syncPosition(position) {
		const seconds = position / 1e3;
		if (this.#seeking) {
			if (seconds < Math.floor(this.#currentTime) || seconds > this.#currentTime + SNAPSHOT_INTERVAL) return;
			this.#seeking = false;
			this.dispatchEvent(new Event("seeked"));
		}
		if (seconds === this.#currentTime) return;
		if (Math.ceil(seconds) < this.#duration) {
			this.#closeToEnded = false;
			this.#ended = false;
		}
		this.#currentTime = seconds;
		this.dispatchEvent(new Event("timeupdate"));
	}
	#syncPlayState(data) {
		if (!this.#paused && data.isPaused) {
			if (data.isBuffering && !this.#pauseRequested) {
				if (this.#waiting) return false;
				this.#waiting = true;
				this.dispatchEvent(new Event("waiting"));
				return true;
			}
			this.#pauseRequested = false;
			this.#waiting = false;
			this.#paused = true;
			this.dispatchEvent(new Event("pause"));
			return true;
		}
		if (this.#paused && this.#isStarting(data)) {
			this.#pauseRequested = false;
			this.#paused = false;
			this.#ended = false;
			this.dispatchEvent(new Event("play"));
			this.#waiting = data.isBuffering;
			if (!this.#waiting) this.#readyState = READY_STATE_HAVE_FUTURE_DATA;
			this.dispatchEvent(new Event(this.#waiting ? "waiting" : "playing"));
			return true;
		}
		if (this.#waiting && !data.isPaused) {
			this.#waiting = false;
			this.#readyState = READY_STATE_HAVE_FUTURE_DATA;
			this.dispatchEvent(new Event("playing"));
			return true;
		}
		return false;
	}
	#checkEnded() {
		if (this.#paused || this.#seeking || this.#closeToEnded) return;
		if (!(this.#duration > 0) || !Number.isFinite(this.#duration)) return;
		if (Math.ceil(this.#currentTime) < this.#duration) return;
		this.#closeToEnded = true;
		if (this.#loop) {
			this.currentTime = REPLAY_POSITION;
			return;
		}
		this.#paused = true;
		this.#ended = true;
		this.pause();
		this.dispatchEvent(new Event("pause"));
		this.dispatchEvent(new Event("ended"));
	}
};
function createControllerPlaceholder() {
	return globalThis.document.createElement("div");
}
function createControllerPlaceholderFrame() {
	return globalThis.document.createElement("iframe");
}
function embedOptionsOf(src) {
	const [, query] = src.split("?");
	const params = new URLSearchParams(query);
	params.delete("t");
	params.sort();
	return `${isVideoEmbed(src) ? "video" : "audio"}?${params}`;
}
function embedPathOf(src) {
	return src.split("?")[0] ?? "";
}
function isVideoEmbed(src) {
	return embedPathOf(src).endsWith("/video");
}
const READY_STATE_HAVE_NOTHING = 0;
const READY_STATE_HAVE_METADATA = 1;
const READY_STATE_HAVE_FUTURE_DATA = 3;
const REPLAY_POSITION = 1;
const SNAPSHOT_INTERVAL = 1;
//#endregion
export { SpotifyAdapter };
